import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody } from "reactstrap";
import "./ErrorFallback.css";

interface ErrorFallbackProps {
    error: Error;
    resetErrorBoundary: () => void;
}

export function CustomErrorBoundary({
    error,
    resetErrorBoundary,
}: ErrorFallbackProps) {
    const [time, setTime] = useState(new Date());

    let t: (key: string) => string;
    try {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        t = useTranslation().t;
    } catch {
        t = (key: string) => key;
    }

    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const formattedTime = time.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });

    const handleReload = () => {
        window.location.reload();
    };

    const handleGoHome = () => {
        try {
            resetErrorBoundary();
        } catch (err) {
            console.error("Error resetting boundary:", err);
        }
        window.location.href = "/home";
    };

    return (
        <Modal
            isOpen
            centered
            size="lg"
            backdrop="static"
            keyboard={false}
            className="error-modal-v3"
            contentClassName="error-modal-content"
        >
            <ModalBody className="p-0">
                <div className="error-card-v3">
                    <div className="error-header-v3">
                        <div className="error-topbar">
                            <div className="error-logo-section">
                                <div className="error-logo-icon">
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path d="M9 12l2 2 4-4" />
                                        <rect
                                            x="3"
                                            y="3"
                                            width="18"
                                            height="18"
                                            rx="4"
                                        />
                                    </svg>
                                </div>
                                <div className="error-logo-text">
                                    <span className="error-logo-title">
                                        ETK SETTING
                                    </span>
                                    <span className="error-logo-subtitle">
                                        Softavera
                                    </span>
                                </div>
                            </div>
                            <div className="error-status-badge">
                                <span className="error-status-dot"></span>
                                <span>{t("Incident detected")}</span>
                            </div>
                        </div>
                        <div className="error-visual-v3">
                            <div className="error-ring-outer"></div>
                            <div className="error-ring-middle"></div>
                            <div className="error-ring-inner">
                                <svg
                                    className="error-icon"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 8v4M12 16h.01" />
                                </svg>
                                <span className="error-code">500</span>
                            </div>
                            <div className="error-dot error-dot-1"></div>
                            <div className="error-dot error-dot-2"></div>
                            <div className="error-dot error-dot-3"></div>
                        </div>
                    </div>
                    <div className="error-content-v3">
                        <h1 className="error-title-v3">
                            <span className="error-title-gradient">
                                {t("Oops!")}
                            </span>{" "}
                            {t("Something went wrong")}
                        </h1>
                        <p className="error-description-v3">
                            {t(
                                "We apologize for the inconvenience. A technical issue has occurred while processing your data. Our team has been automatically notified."
                            )}
                        </p>
                        <div className="error-info-grid">
                            <div className="error-info-card">
                                <div className="error-info-icon error-info-icon-green">
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                        <path d="M9 12l2 2 4-4" />
                                    </svg>
                                </div>
                                <div className="error-info-text">
                                    <span className="error-info-title">
                                        {t("Data secured")}
                                    </span>
                                    <span className="error-info-subtitle">
                                        {t("Your information is protected")}
                                    </span>
                                </div>
                            </div>
                            <div className="error-info-card">
                                <div className="error-info-icon error-info-icon-blue">
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M12 6v6l4 2" />
                                    </svg>
                                </div>
                                <div className="error-info-text">
                                    <span className="error-info-title">
                                        {t("Quick resolution")}
                                    </span>
                                    <span className="error-info-subtitle">
                                        {t("Estimated time: ~2 minutes")}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="error-buttons">
                            <button
                                className="error-btn-primary"
                                onClick={handleReload}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                                </svg>
                                <span>{t("Reload Page")}</span>
                            </button>

                            <button
                                className="error-btn-secondary"
                                onClick={handleGoHome}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                    <path d="M9 22V12h6v10" />
                                </svg>
                                <span>{t("Go to Home")}</span>
                            </button>
                        </div>
                        <div className="error-footer">
                            <div className="error-footer-left">
                                <span>© 2026 Softavera</span>
                                <span className="error-footer-dot">•</span>
                                <span>{t("All rights reserved")}</span>
                            </div>
                            <div className="error-footer-time">
                                {formattedTime}
                            </div>
                        </div>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
}
